// Libraries
import {StyleSheet, css} from 'aphrodite/no-important';
import $ from 'jquery';

let Metrics = {};
Metrics.Grid = {
    HorizontalMargin: 16,
    HorizontalGutter: 32,
    VerticalMargin: 16,
    VerticalGutter: 32,
    SidebarWidth: 320,
    FixedWidth: 1120, // 1152 - 2 * Metrics.Grid.HorizontalMargin
};
Metrics.Type = {
    LineHeight: {
        Label: 1.2,
        Paragraph: 1.5,
    },
    Font: {
        // Default
        SansSerifRegular: "Lato",
        SansSerifMedium: "Lato",
        // Serif
        Serif: "minion-pro",
        // Monospace
        Monospace: "Inconsolata",
    },
};

// A singleton utility class to handle form factor changes
export class FormFactorChangeListener {
    static ReferenceBaseFontSize = 20;
    static ReferenceViewportWidth = 1366;
    static ScalingMode = {
        NoScaling: 0,
        Responsive: 1, // Default style
        UniformlyResponsive: 2,
        UniformlyFluid: 3,
    };
    static deviceScreensWithTablet = {
        Phone: {
            Min: 0,
            Max: 720,
        },
        Tablet: {
            Min: 721,
            Max: 1023,
        },
        Desktop: {
            Min: 1024,
            Max: null,
        },
    };
    deviceScreens = FormFactorChangeListener.deviceScreensWithTablet;
    getMediaQuery() {
        return {
            RetinaDevice: "@media only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 13/10), only screen and (min-resolution: 120dpi)", // See: https://gist.github.com/marcedwards/3446599
            Phone: "@media only screen and (max-width: " + this.deviceScreens.Phone.Max + "px)",
            Tablet: "@media only screen and (min-width: " + this.deviceScreens.Tablet.Min + "px) and (max-width: " + this.deviceScreens.Tablet.Max + "px)",
            Desktop: "@media only screen and (min-width: " + this.deviceScreens.Desktop.Min + "px)",
        };
    };
    static ResponsiveBaseFontSize = {
        Desktop: FormFactorChangeListener.ReferenceBaseFontSize, // 20
        Tablet: FormFactorChangeListener.ReferenceBaseFontSize,
        Phone: 18,
    };
    static SidebarBehavior = {
        InGrid: 0, // Default
        Docked: 1,
    };
    static FluidBaseFontSize = FormFactorChangeListener.ReferenceBaseFontSize * (100 / FormFactorChangeListener.ReferenceViewportWidth);
    constructor() {
        // Set up some state variables:
        this.baseFontStyles = StyleSheet.create({
            noScaling: {
                fontSize: FormFactorChangeListener.ReferenceBaseFontSize,
            },
            responsive: {
                [this.getMediaQuery().Desktop]: {
                    fontSize: FormFactorChangeListener.ResponsiveBaseFontSize.Desktop,
                },
                [this.getMediaQuery().Tablet]: {
                    fontSize: FormFactorChangeListener.ResponsiveBaseFontSize.Tablet,
                },
                [this.getMediaQuery().Phone]: {
                    fontSize: FormFactorChangeListener.ResponsiveBaseFontSize.Phone,
                },
            },
            uniformlyFluid: {
                fontSize: FormFactorChangeListener.FluidBaseFontSize + "vw",
            },
        });
        this.cssRules = [];
        this.setScalingMode(FormFactorChangeListener.ScalingMode.Responsive);
        this.setSidebarBehavior(FormFactorChangeListener.SidebarBehavior.InGrid);
    }
    getScalingMode() {
        return this.scalingMode;
    }
    setScalingMode(mode) {
        this.scalingMode = mode;

        // Update the CSS class of the <html> node
        let className = "";
        if (this.scalingMode === FormFactorChangeListener.ScalingMode.NoScaling) {
            className = css(this.baseFontStyles.noScaling);
        } else if (this.scalingMode === FormFactorChangeListener.ScalingMode.Responsive) {
            className = css(this.baseFontStyles.responsive);
        } else if (this.scalingMode === FormFactorChangeListener.ScalingMode.UniformlyResponsive) {
            className = css(this.baseFontStyles.responsive);
        } else if (this.scalingMode === FormFactorChangeListener.ScalingMode.UniformlyFluid) {
            className = css(this.baseFontStyles.uniformlyFluid);
        }
        document.documentElement.className = className;
    }
    // Scaling function for converting a reference form factor dimension (ie, at desktop scale) to a scalable dimension (in rems)
    scaleUIDimension(px) {
        if (this.scalingMode === FormFactorChangeListener.ScalingMode.Responsive) {
            return px;
        } else {
            let pxValue = null;
            if (typeof(px) === "string") {
                let matches = null;
                if (matches === px.match(/^([0-9.]+)px$/)) {
                    pxValue = Number.parseFloat(matches[1]);
                } else if (matches === px.match(/^([0-9.]+)/)) {
                    pxValue = Number.parseFloat(matches[1]);
                }
            } else if (typeof(px) === "number") {
                pxValue = px;
            }
            if (pxValue === null) {
                console.log("Unable to parse \"" + px + "\" as a px value");
                return null;
            }
            const rem = pxValue / FormFactorChangeListener.ReferenceBaseFontSize + "rem";
            return rem;
        }
    }
    scaleUIDimensions(responsive) {
        if (typeof(responsive) === "object") {
            if (this.scalingMode !== FormFactorChangeListener.ScalingMode.Responsive) {
                const mediaQueries = Object.keys(responsive);
                const firstMediaQuery = mediaQueries[0];
                const firstValue = responsive[firstMediaQuery];
                return this.scaleUIDimension(firstValue);
            } else {
                return responsive;
            }
        } else {
            const px = responsive;
            return this.scaleUIDimension(px);
        }
    }
    /**
     * Convert a style that contains responsive properties into an aphrodite style. For example, the following object:
     * {
     *   height: {
     *     "@media only screen and (min-width: 768px)": 40,
     *     "@media only screen and (max-width: 767px)": 32
     *   }
     * }
     * would be converted into the following object:
     * {
     *   "@media only screen and (min-width: 768px)": {
     *     height: 40
     *   },
     *   "@media only screen and (max-width: 767px)": {
     *     height: 32
     *   },
     * }
     */
    expandEmbeddedMediaQueries(style) {
        const aphroditeStyle = {};
        for (let key in style) {
            const value = style[key];
            if (key.startsWith("@")) {
                const query = key;
                const addition = {
                    [query]: value,
                };
                $.extend(true, aphroditeStyle, addition);
            } else if (key.startsWith(":")) {
                const pseudo = key;
                const pseudoStyles = value;
                const pseudoAddition = {
                    [pseudo]: {},
                };
                for (let property in pseudoStyles) {
                    const subValue = pseudoStyles[property];
                    if (typeof(subValue) === "object") {
                        const responsiveValues = subValue;
                        for (let query in responsiveValues) {
                            const responsiveValue = responsiveValues[query];
                            const addition = {
                                [query]: {
                                    [pseudo]: {
                                        [property]: responsiveValue,
                                    },
                                },
                            };
                            $.extend(true, aphroditeStyle, addition);
                        }
                    } else {
                        const propertyValue = subValue;
                        const addition = {
                            [pseudo]: {
                                [property]: propertyValue,
                            },
                        };
                        $.extend(true, pseudoAddition, addition);
                    }
                    $.extend(true, aphroditeStyle, pseudoAddition);
                }
            } else {
                const property = key;
                if (typeof(value) === "object") {
                    const responsiveValues = value;
                    for (let query in responsiveValues) {
                        const responsiveValue = responsiveValues[query];
                        const addition = {
                            [query]: {
                                [property]: responsiveValue,
                            },
                        };
                        $.extend(true, aphroditeStyle, addition);
                    }
                } else {
                    const addition = {
                        [property]: value,
                    };
                    $.extend(true, aphroditeStyle, addition);
                }
            }
        }
        return aphroditeStyle;
    }
    getSidebarBehavior() {
        return this.sidebarBehavior;
    }
    setSidebarBehavior(behavior) {
        this.sidebarBehavior = behavior;
    }
}
export const sharedFormFactorChangeListener = new FormFactorChangeListener();
export const expandEmbeddedMediaQueries = sharedFormFactorChangeListener.expandEmbeddedMediaQueries;

Metrics.DeviceScreens = () => {
    return {
        Phone: {
            Min: 0,
            Max: 720,
        },
        Tablet: {
            Min: 721,
            Max: 1023,
        },
        Desktop: {
            Min: 1024,
            Max: null,
        },
    }
};
Metrics.MediaQuery = () => sharedFormFactorChangeListener.getMediaQuery();

/**
 *
 * A method for calling expandEmbeddedMediaQueries on each style in a styles
 * object and creating an Aphrodite stylesheet from that result.
 *
 * Example usage:
 * const styles = createExpandableMediaQueryStylesheet({
 *     container: {
 *         width: 1256,
 *     },
 *     item: {
 *         margin: '0 auto',
 *     },
 * });
 *
 */
export const createExpandableMediaQueryStylesheet = (styles) => {
    const updatedStyles = Object.keys(styles).reduce((newStyles, className) => {
        newStyles[className] = expandEmbeddedMediaQueries(styles[className]);
        return newStyles;
    }, {});
    return StyleSheet.create(updatedStyles);
};

// Helper functions
const PhoneScaleFactor = 0.8;
const TabletScaleFactor = 0.9;
const createUIDimensionScalingFunction = (desktopValue) => {
    return () => {
        return sharedFormFactorChangeListener.scaleUIDimensions({
            [Metrics.MediaQuery().Desktop]: desktopValue,
            [Metrics.MediaQuery().Tablet]: Math.round(desktopValue * TabletScaleFactor),
            [Metrics.MediaQuery().Phone]: Math.round(desktopValue * PhoneScaleFactor),
        });
    };
};
const createResponsiveUIDimensionFunction = (phoneValue, tabletValue, desktopValue) => {
    return () => {
        return sharedFormFactorChangeListener.scaleUIDimensions({
            [Metrics.MediaQuery().Phone]: phoneValue,
            [Metrics.MediaQuery().Tablet]: tabletValue,
            [Metrics.MediaQuery().Desktop]: desktopValue,
        });
    };
};


// Computed constants
Metrics.CornerRadius = sharedFormFactorChangeListener.scaleUIDimension(4);
Metrics.ElementSpacing = {
    XXSmall: createUIDimensionScalingFunction(4),
    XSmall: createUIDimensionScalingFunction(8),
    Small: createUIDimensionScalingFunction(12),
    Medium: createUIDimensionScalingFunction(16),
    Large: createUIDimensionScalingFunction(24), // TODO: We may end up removing this size
    XLarge: createUIDimensionScalingFunction(32),
    XXLarge: createUIDimensionScalingFunction(48), // TODO: We may end up removing this size
    XXXLarge: createUIDimensionScalingFunction(64),
};
Metrics.ElementPadding = { // TODO: We should probably merge this into the previous property since they're the same sizes
    XXSmall: createUIDimensionScalingFunction(4),
    XSmall: createUIDimensionScalingFunction(8),
    Small: createUIDimensionScalingFunction(12),
    Medium: createUIDimensionScalingFunction(16),
    Large: createUIDimensionScalingFunction(24), // TODO: We may end up removing this size
    XLarge: createUIDimensionScalingFunction(32),
    XXLarge: createUIDimensionScalingFunction(48), // TODO: We may end up removing this size
    XXXLarge: createUIDimensionScalingFunction(64),
};
Metrics.ElementHeight = {
    XSmall: createUIDimensionScalingFunction(32),
    Small: createUIDimensionScalingFunction(40),
    Medium: createUIDimensionScalingFunction(48),
    Large: createUIDimensionScalingFunction(60),
};
Metrics.IconSize = {
    XXSmall: createUIDimensionScalingFunction(8),
    XSmall: createUIDimensionScalingFunction(12),
    Small: createUIDimensionScalingFunction(16),
    Medium: createUIDimensionScalingFunction(24),
    Large: createUIDimensionScalingFunction(32),
    XLarge: createUIDimensionScalingFunction(48),
    XXLarge: createUIDimensionScalingFunction(64),
};
Metrics.Type.SansSerif = {
    // Note: You'll see duplicate values below because we haven't settled on separate tablet and phone metrics yet
    XXLarge: {
        Size: createResponsiveUIDimensionFunction(28, 28, 36),
        LineHeight: createResponsiveUIDimensionFunction("32px", "32px", "40px"),
    },
    XLarge: {
        Size: createResponsiveUIDimensionFunction(22, 22, 28),
        LineHeight: createResponsiveUIDimensionFunction("26px", "26px", "32px"),
    },
    Large: {
        Size: createResponsiveUIDimensionFunction(20, 20, 20),
        LineHeight: createResponsiveUIDimensionFunction("24px", "24px", "24px"),
    },
    Medium: {
        Size: createResponsiveUIDimensionFunction(16, 16, 16),
        LineHeight: createResponsiveUIDimensionFunction("22px", "22px", "22px"),
    },
    Small: {
        Size: createResponsiveUIDimensionFunction(14, 14, 14),
        LineHeight: createResponsiveUIDimensionFunction("18px", "18px", "18px"),
    },
    XSmall: {
        Size: createResponsiveUIDimensionFunction(12, 12, 12),
        LineHeight: createResponsiveUIDimensionFunction("18px", "18px", "18px"),
    },
};
Metrics.Type.Serif = {
    // Note: You'll see duplicate values below because we haven't settled on separate tablet and phone metrics yet
    Large: {
        Size: createResponsiveUIDimensionFunction(22, 22, 22),
        LineHeight: createResponsiveUIDimensionFunction("28px", "28px", "28px"),
    },
    Medium: {
        Size: createResponsiveUIDimensionFunction(18, 18, 18),
        LineHeight: createResponsiveUIDimensionFunction("22px", "22px", "22px"),
    },
};
Metrics.Type.Monospace = {
    // Note: You'll see duplicate values below because we haven't settled on separate tablet and phone metrics yet
    Medium: {
        Size: createResponsiveUIDimensionFunction(17, 17, 17),
        LineHeight: createResponsiveUIDimensionFunction("22px", "22px", "22px"),
    },
};
/**
 * A method that accepts a 3 or 6 digit color hex code and an opacity percentage
 * and converts them into an rbga string to insert in css styling.
 *
 * This was shamelessly ripped off from Stack Overflow
 * https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb#5624139
 * and explanded to return a css-ready rgba string.
 */
Metrics.HexToRgba = (hex, opacity) => {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function(m, r, g, b) {
        return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (!result) {
        return null;
    }

    const getValue = (index) => parseInt(result[index], 16);

    return `rgba(${getValue(1)}, ${getValue(2)}, ${getValue(3)}, ${opacity})`;
};
Metrics.Color = {
    None: "rgba(0, 0, 0, 0)",
    Debug: "#FF0088",
    DebugFaint: "rgba(255, 0, 128, 0.08)",
    Hairline: "rgba(0, 0, 0, 0.1)",
    HairlineRetina: "rgba(0, 0, 0, 0.2)",
    Shadow: "rgba(0, 0, 0, 0.2)",
    Neutral: {
        White: "#FFF",
        OffWhite: "#FAFAFA",
        DarkGray: "#626569",
        // TODO(diedra): We gotta come up with a better way to name colors.
        XDarkGray: "#314453",
        OffBlack: "#21242C",
        Black: "#000",
    },
    Admin: {
        Control1: "#1B6CE1",
        Control2: "#66AFE9",
        Control3: "#1E79FB",
        Control4: "#1351A8",
    },
};
Metrics.Grid.Width = {
    OneHalf: "calc((100% - " + Metrics.Grid.HorizontalGutter + "px) / 2)",
    OneThird: "calc(1 * (100% - 2 * " + Metrics.Grid.HorizontalGutter + "px) / 3)",
    OneFourth: "calc(1 * (100% - 3 * " + Metrics.Grid.HorizontalGutter + "px) / 4)",
    OneSixth: "calc(1 * (100% - 5 * " + Metrics.Grid.HorizontalGutter + "px) / 6)",
    OneEighth: "calc(1 * (100% - 7 * " + Metrics.Grid.HorizontalGutter + "px) / 8)",
    OneNinth: "calc(1 * (100% - 8 * " + Metrics.Grid.HorizontalGutter + "px) / 9)",
    OneTwelfth: "calc(1 * (100% - 11 * " + Metrics.Grid.HorizontalGutter + "px) / 12)",
};
Metrics.Grid.ColumnCount = () => {
    return {
        Desktop: 12,
        Tablet: 12,
        Phone: 4,
    };
};
Metrics.Color.LightUI = {
    PrimaryBG: Metrics.Color.Neutral.White,
    SecondaryBG: Metrics.Color.Neutral.OffWhite,
    PrimaryText: Metrics.Color.Neutral.OffBlack,
    SecondaryText: Metrics.Color.Neutral.DarkGray,
    PlaceholderText: Metrics.Color.Neutral.DarkGray,
};
Metrics.Color.Control = {
    Default: Metrics.Color.Admin.Control1,
    Hover: Metrics.Color.Admin.Control3,
    Active: Metrics.Color.Admin.Control4,
};
/**
 * These colors are associated with external brands that we don't control.
 */
Metrics.Color.ExternalBrands = {
    Google: "#BE2612",
    Facebook: "#2671B7",
};
/**
 * These colors are associated with the login flow, including the logged-out
 * homepage, sign up, and login pages.
 */
Metrics.Color.LoggedOut = {
    BGBlue: "#066ee2",
    BGGreen: "#63d9ea",
    DarkBlue: "#00294a",
    NeutralBlue: "#1a4464",
};

const testStyle = {
    display: "flex",
    ":hover": {
        border: "1px solid red",
    },
    ":focus": {
        backgroundColor: {
            "@media only screen and (max-width: 767px)": "red",
            "@media only screen and (min-width: 768px)": "blue",
        },
    },
    "@media only screen and (max-width: 767px)": {
        height: 32,
        ":focus": {
            outline: "1px solid red",
        },
    },
    "@media only screen and (min-width: 768px)": {
        height: 40,
        ":focus": {
            outline: "1px solid blue",
        },
    },
    padding: {
        "@media only screen and (max-width: 767px)": 12,
        "@media only screen and (min-width: 768px)": 16,
    },
    borderWidth: {
        "@media": 1,
        "@media only screen and (-webkit-min-device-pixel-ratio: 2)": 0.5,
    },
};
expandEmbeddedMediaQueries(testStyle);

const CoreMetrics = Metrics;
export default CoreMetrics;



// WEBPACK FOOTER //
// ./src/core/Metrics.js